import { useMemo, useState, type ReactNode } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Check,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { SortableItem } from "./sortable-item";

export type TreeItem = { id: string; title: string };

type TreeNodeProps<T extends TreeItem> = {
  items: T[];
  label: string;
  addLabel: string;
  emptyLabel: string;
  onCreate?: () => void;
  onRename: (id: string, title: string) => Promise<boolean> | boolean;
  onDelete: (id: string) => void;
  /** Ignored when `contextless` — the parent commits the drop instead. */
  onReorder?: (ids: string[]) => void;
  /** Rendered under the row when it is expanded. Omit for leaf rows. */
  childComponent?: (item: T) => ReactNode;
  itemIcon?: (item: T) => ReactNode;
  itemBadge?: (item: T) => ReactNode;
  /** Optional extra row action, rendered next to rename and delete. */
  itemAction?: (item: T) => {
    label: string;
    icon: ReactNode;
    run: () => void;
  };
  selectedId?: string;
  onSelect?: (item: T) => void;
  disabled?: boolean;
  /** Visual nesting depth; only affects indentation. */
  depth?: number;
  /**
   * Render a bare `SortableContext` and let an ancestor own the `DndContext`.
   * Lessons use this so one context spans every module and an item can be
   * dragged from one list into another.
   */
  contextless?: boolean;
  /**
   * Droppable id for the empty state, so a list with no items is still a
   * valid drop target. Only meaningful together with `contextless`.
   */
  emptyDroppableId?: string;
};

/**
 * Generic sortable tree level. Nests through `childComponent`, so the same
 * component renders both modules and lessons.
 *
 * When it owns the `DndContext`, ordering is optimistic: the drop is reflected
 * locally and `onReorder` fires once. The local order is dropped as soon as the
 * incoming `items` no longer match it, so props stay authoritative without the
 * tree ever remounting.
 */
export function TreeNode<T extends TreeItem>({
  items,
  label,
  addLabel,
  emptyLabel,
  onCreate,
  onRename,
  onDelete,
  onReorder,
  childComponent,
  itemIcon,
  itemBadge,
  itemAction,
  selectedId,
  onSelect,
  disabled = false,
  depth = 0,
  contextless = false,
  emptyDroppableId,
}: TreeNodeProps<T>) {
  const [order, setOrder] = useState<string[] | null>(null);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const sensors = useSensors(
    // A small activation distance keeps a click on the grip from registering
    // as a zero-length drag, which would cancel the row's own click handlers.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const ordered = useMemo(() => {
    // In contextless mode the parent already hands items down in drop order.
    if (contextless || !order) return items;
    const byId = new Map(items.map((item) => [item.id, item]));
    const mapped = order
      .map((id) => byId.get(id))
      .filter((item): item is T => item !== undefined);
    return mapped.length === items.length ? mapped : items;
  }, [contextless, items, order]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (disabled || !over || active.id === over.id) return;
    const from = ordered.findIndex((item) => item.id === active.id);
    const to = ordered.findIndex((item) => item.id === over.id);
    if (from < 0 || to < 0) return;
    const ids = arrayMove(ordered, from, to).map((item) => item.id);
    setOrder(ids);
    onReorder?.(ids);
  };

  const startRename = (item: T) => {
    setEditingId(item.id);
    setDraft(item.title);
  };
  const commitRename = async (id: string) => {
    const title = draft.trim();
    if (!title) return setEditingId(null);
    const saved = await onRename(id, title);
    if (saved) setEditingId(null);
  };

  const rows = (
    <SortableContext
      items={ordered.map((item) => item.id)}
      strategy={verticalListSortingStrategy}
    >
      <ul className="space-y-2">
        {ordered.map((item) => {
          const isCollapsed = collapsed.has(item.id);
          const isSelected = selectedId === item.id;
          const isEditing = editingId === item.id;
          return (
            <SortableItem key={item.id} id={item.id}>
              {({ attributes, listeners, setNodeRef, style }) => (
                <li
                  ref={setNodeRef}
                  style={style}
                  className={`border-ink border-chip ${isSelected ? "bg-acid" : "bg-paper"}`}
                >
                  <div className="flex items-center gap-2 p-2">
                    <span
                      {...listeners}
                      {...attributes}
                      aria-label={`Reordenar ${item.title}`}
                      className={`text-ink-soft ${disabled ? "cursor-progress opacity-50" : "cursor-grab active:cursor-grabbing"}`}
                    >
                      <GripVertical size={16} />
                    </span>

                    {childComponent && (
                      <button
                        type="button"
                        aria-label={isCollapsed ? "Expandir" : "Contraer"}
                        onClick={() =>
                          setCollapsed((current) => {
                            const next = new Set(current);
                            if (!next.delete(item.id)) next.add(item.id);
                            return next;
                          })
                        }
                      >
                        {isCollapsed ? (
                          <ChevronRight size={15} />
                        ) : (
                          <ChevronDown size={15} />
                        )}
                      </button>
                    )}

                    {itemIcon?.(item)}

                    {isEditing ? (
                      <input
                        autoFocus
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            commitRename(item.id);
                          }
                          if (event.key === "Escape") setEditingId(null);
                        }}
                        className="border-ink bg-bone border-chip flex-1 px-2 py-1 text-sm outline-none"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => onSelect?.(item)}
                        onDoubleClick={() => startRename(item)}
                        className="font-display flex-1 truncate text-left text-sm font-bold"
                      >
                        {item.title}
                      </button>
                    )}

                    {!isEditing && itemBadge?.(item)}

                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          aria-label="Guardar nombre"
                          onClick={() => commitRename(item.id)}
                        >
                          <Check size={15} />
                        </button>
                        <button
                          type="button"
                          aria-label="Cancelar"
                          onClick={() => setEditingId(null)}
                        >
                          <X size={15} />
                        </button>
                      </>
                    ) : (
                      <>
                        {itemAction && (
                          <button
                            type="button"
                            aria-label={itemAction(item).label}
                            disabled={disabled}
                            onClick={() => itemAction(item).run()}
                            className="disabled:opacity-50"
                          >
                            {itemAction(item).icon}
                          </button>
                        )}
                        <button
                          type="button"
                          aria-label={`Renombrar ${item.title}`}
                          disabled={disabled}
                          onClick={() => startRename(item)}
                          className="disabled:opacity-50"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          aria-label={`Eliminar ${item.title}`}
                          disabled={disabled}
                          onClick={() => onDelete(item.id)}
                          className="disabled:opacity-50"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>

                  {childComponent && !isCollapsed && (
                    <div className="border-ink bg-bone border-t-(length:--border-chip) p-3 pl-6">
                      {childComponent(item)}
                    </div>
                  )}
                </li>
              )}
            </SortableItem>
          );
        })}
      </ul>
    </SortableContext>
  );

  return (
    <div className={depth > 0 ? "mt-3" : ""}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="font-mono text-xs font-bold uppercase tracking-widest">
          {label} ({items.length})
        </p>
        {onCreate && (
          <button
            type="button"
            onClick={onCreate}
            disabled={disabled}
            className="border-ink bg-lemon font-display border-chip flex items-center gap-1 px-2 py-1 text-xs font-bold disabled:opacity-50"
          >
            <Plus size={13} /> {addLabel}
          </button>
        )}
      </div>

      {ordered.length === 0 ? (
        <EmptyDrop droppableId={emptyDroppableId}>{emptyLabel}</EmptyDrop>
      ) : contextless ? (
        rows
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          {rows}
        </DndContext>
      )}
    </div>
  );
}

/**
 * Empty-state placeholder that doubles as a drop target, so a module with no
 * lessons can still receive one dragged from another module.
 */
function EmptyDrop({
  droppableId,
  children,
}: {
  droppableId?: string;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: droppableId ?? "unused" });
  return (
    <p
      ref={droppableId ? setNodeRef : undefined}
      className={`border-ink text-ink-soft border-chip border-dashed p-3 text-sm ${isOver && droppableId ? "bg-acid" : ""}`}
    >
      {children}
    </p>
  );
}
